
import React from 'react';
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, FreeMode, Pagination, Mousewheel } from 'swiper/modules';

// Swiper Styles
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/pagination';

const TEAM_DATA = [
    { id: 1, name: "SHIVANI KUMARI", role: "FOUNDER & CEO", image: "./OurTeams/shivani.jpeg" },
    { id: 2, name: "PRASHANT SAGAR", role: "WEB DEVELOPER", image: "./OurTeams/prashnat.jpeg" },
    { id: 3, name: "MUKESH KUMAR", role: "PROJECT MANAGER", image: "./OurTeams/project-manager.jpeg" },
    { id: 4, name: "SHIVU CINE", role: "VIDEO EDITOR", image: "./OurTeams/vdoEdi.jpeg" },
    { id: 5, name: "KANCHAN TEGTA", role: "SALES DEPARTMENT HEAD", image: "./OurTeams/sales.jpeg" },
];

const OurTeams = () => {
    return (
        <section className="py-24 bg-white overflow-hidden selection:bg-black selection:text-white">
            <div className="max-w-[1600px] mx-auto px-6">
                
                {/* --- MINIMALIST HEADER --- */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-28 border-b border-gray-100 pb-16">
                    <div className="lg:col-span-8">
                        <motion.div
                            initial={{ clipPath: 'inset(100% 0 0 0)' }}
                            whileInView={{ clipPath: 'inset(0% 0 0 0)' }}
                            transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
                        >
                            <h2 className="text-[12vw] lg:text-[8vw] leading-[0.85] font-black tracking-tighter text-secondary uppercase">
                                MEET<span className="text-gray-200 ">OUR</span> <br /> 
                                <span className="italic font-light text-primary">TEAM.</span>
                            </h2>
                        </motion.div>
                    </div>
                    
                    <div className="lg:col-span-4 flex items-end">
                        <motion.div 
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5 }}
                            className="relative"
                        >
                            <p className="text-xs font-bold tracking-[0.4em] uppercase text-gray-400 mb-6">Expertise & Vision</p>
                            <p className="text-xl font-light leading-snug text-gray-800">
                              At<span className="font-bold"> DIGITAL CANTO,</span> our leadership team brings a wealth of experience, strategic vision, and an unwavering commitment to excellence. They guide our mission, foster our culture of innovation, and ensure we consistently deliver outstanding results for our global clientele.
                            </p>
                            <div className="mt-8 h-[2px] w-20 bg-black" />
                        </motion.div>
                    </div>
                </div>

                {/* --- THE ART GALLERY SLIDER --- */}
                <Swiper
                    modules={[Autoplay, FreeMode, Pagination, Mousewheel]}
                    spaceBetween={40}
                    slidesPerView={1.2}
                    freeMode={true}
                    mousewheel={{ forceToAxis: true }}
                    loop={true}
                    speed={1000}
                    autoplay={{ delay: 3000, disableOnInteraction: false }}
                    pagination={{ clickable: true, dynamicBullets: true }}
                    breakpoints={{
                        640: { slidesPerView: 2.2 },
                        1024: { slidesPerView: 3.5 },
                        1400: { slidesPerView: 4.2 },
                    }}
                    className="!pb-24 overflow-visible"
                >
                    {TEAM_DATA.map((member, index) => (
                        <SwiperSlide key={member.id}>
                            <motion.div 
                                initial={{ opacity: 0, scale: 0.95 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ delay: index * 0.1 }}
                                className="group relative bg-white"
                            >
                                <div className="relative aspect-[3/4] overflow-hidden rounded-xl shadow-[0_10px_30px_rgba(0,0,0,0.05)] transition-all duration-700 group-hover:shadow-[0_30px_60px_rgba(0,0,0,0.12)]">
                                    <motion.img 
                                        src={member.image} 
                                        alt={member.name} 
                                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                                    />

                                    <div className="absolute inset-0 bg-gradient-to-t from-white/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                </div>


                                <div className="mt-8 transform transition-all duration-500 group-hover:translate-x-2">
                                    <div className="flex items-center space-x-3 mb-2">
                                        <span className="h-[1px] w-6 bg-gray-300 group-hover:w-12 group-hover:bg-black transition-all duration-500" />
                                        <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-400">
                                            {member.role}
                                        </p>
                                    </div>
                                    <h3 className="text-3xl font-bold text-secondary tracking-tighter leading-none">
                                        {member.name.split(' ')[0]} <br />
                                        <span className="text-gray-300 group-hover:text-primary transition-colors duration-500 font-semibold">
                                            {member.name.split(' ')[1]}
                                        </span>
                                    </h3>
                                </div>
                                <motion.div 
                                    whileHover={{ scale: 1.1 }}
                                    className="absolute top-4 right-4 w-12 h-12 rounded-full bg-black/5 backdrop-blur-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500"
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2">
                                        <path d="M5 12h14M12 5l7 7-7 7" />
                                    </svg>
                                </motion.div>
                            </motion.div>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>
            <style jsx global>{`
                .swiper-pagination-bullet { 
                    background: #e5e7eb; 
                    opacity: 1; 
                    height: 4px; 
                    width: 20px; 
                    border-radius: 2px;
                    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                }
                .swiper-pagination-bullet-active { 
                    background: #000 !important; 
                    width: 60px !important; 
                }
            `}</style>
        </section>
    );
}

export default OurTeams;